import { addHours } from 'date-fns';

interface ParsedEvent {
  title: string;
  start: Date;
  end: Date;
  description?: string;
}

/**
 * Parse event text in various formats and return structured event objects
 * 
 * Supported formats:
 * - Wed, July 9 – 10am MT (The Sales Process)
 * - July 9, 2025 – 10am MT (The Sales Process)
 * - 7/9/2025 10am MT (The Sales Process)
 * - Saturday, February 8, 2025 5:30-8 p.m. (Event Title)
 * - Unstructured text with event name, date and time information
 */
export function parseEventText(text: string): ParsedEvent[] {
  const events: ParsedEvent[] = [];
  
  // Check if text contains bullet points or numbered lists
  const hasBulletPoints = /^[\s•\-*]+/m.test(text);
  const hasNumberedList = /^\s*\d+\.\s+/m.test(text);
  
  if (hasBulletPoints || hasNumberedList) {
    // Split by bullet points or numbered items
    const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
    
    for (const line of lines) {
      // Remove bullet point or number prefix
      const cleanLine = line.replace(/^[\s•\-*]+|^\s*\d+\.\s+/, '').trim();
      if (cleanLine) {
        try {
          const event = parseSingleEvent(cleanLine);
          if (event) {
            events.push(event);
          }
        } catch (error) {
          console.warn(`Failed to parse event line: "${cleanLine}"`, error);
        }
      }
    }
    
    return events;
  }
  
  // Split by double newlines to separate events
  const eventBlocks = text.split(/\n\s*\n/).filter(block => block.trim());
  
  if (eventBlocks.length > 1) {
    // Multiple events separated by blank lines
    for (const block of eventBlocks) {
      try {
        const event = parseEventBlock(block);
        if (event) {
          events.push(event);
        }
      } catch (error) {
        console.warn(`Failed to parse event block: "${block}"`, error);
      }
    }
  } else {
    // Single event or multiple events without clear separation
    // Try to split by newlines and parse each line as a separate event
    const lines = text.split('\n').filter(line => line.trim());
    
    if (lines.length > 1) {
      // This might be a single unstructured event with multiple lines
      try {
        const event = parseUnstructuredEvent(text);
        if (event) {
          events.push(event);
        } else {
          // If unstructured parsing fails, try each line as a separate event
          for (const line of lines) {
            try {
              const event = parseSingleEvent(line);
              if (event) {
                events.push(event);
              }
            } catch (error) {
              console.warn(`Failed to parse event line: "${line}"`, error);
            }
          }
        }
      } catch (error) {
        console.warn(`Failed to parse unstructured event: "${text}"`, error);
      }
    } else {
      // Single line event
      try {
        const event = parseSingleEvent(text);
        if (event) {
          events.push(event);
        }
      } catch (error) {
        console.warn(`Failed to parse event text: "${text}"`, error);
      }
    }
  }

  return events;
}

/**
 * Parse unstructured event text with multiple lines
 * Example:
 * Bowties & Beauties Sweetheart Dance
 * Saturday, February 8, 2025
 * 5:30-8 p.m.
 * $30 Res | $39 Non Res
 * Registration on RecTrac
 */
function parseUnstructuredEvent(text: string): ParsedEvent | null {
  const lines = text.split('\n').map(line => line.trim()).filter(line => line);
  
  if (lines.length < 2) return null;
  
  // First line is usually the title
  const title = lines[0];
  
  // Look for date in the text
  let dateStr = '';
  let dateLineIndex = -1;
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    // Check for date patterns
    if (
      /\b(January|February|March|April|May|June|July|August|September|October|November|December)\b/i.test(line) ||
      /\b(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\b/i.test(line) ||
      /\b\d{1,2}\/\d{1,2}\/\d{4}\b/.test(line) ||
      /\b\d{4}-\d{2}-\d{2}\b/.test(line)
    ) {
      dateStr = line;
      dateLineIndex = i;
      break;
    }
  }
  
  if (!dateStr || dateLineIndex === -1) return null;
  
  // Look for time information, usually in the line after the date
  let timeStr = '';
  let timeLineIndex = -1;
  
  for (let i = dateLineIndex + 1; i < lines.length; i++) {
    const line = lines[i];
    // Check for time patterns
    if (
      /\d{1,2}[:]\d{2}/.test(line) || // 5:30
      /\d{1,2}[-]\d{1,2}/.test(line) || // 5-8
      /\d{1,2}[ ]?[ap]\.?m\.?/i.test(line) // 5 pm or 5p.m.
    ) {
      timeStr = line;
      timeLineIndex = i;
      break;
    }
  }
  
  // If we found both date and time
  if (dateStr && timeStr) {
    // Parse date
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    
    // Parse time
    // Look for time range patterns like "5:30-8 p.m." or "5:30 p.m. - 8:00 p.m."
    const timeRangeRegex = /(\d{1,2}(?::\d{2})?)\s*(?:-|to)\s*(\d{1,2}(?::\d{2})?)\s*([ap]\.?m\.?)(?:\s+([A-Za-z]{2,3}))?/i;
    const timeRangeMatch = timeStr.match(timeRangeRegex);
    
    if (timeRangeMatch) {
      const [_, startTime, endTime, ampm, timezone] = timeRangeMatch;
      
      // Parse start time
      const start = new Date(date);
      const [startHours, startMinutes] = startTime.split(':').map(part => parseInt(part, 10));
      const isPM = /p/i.test(ampm);
      
      start.setHours(
        isPM && startHours < 12 ? startHours + 12 : startHours,
        startMinutes || 0,
        0,
        0
      );
      
      // Parse end time
      const end = new Date(date);
      const [endHours, endMinutes] = endTime.split(':').map(part => parseInt(part, 10));
      
      end.setHours(
        isPM && endHours < 12 ? endHours + 12 : endHours,
        endMinutes || 0,
        0,
        0
      );
      
      // Adjust for timezone if specified
      if (timezone) {
        const { adjustedStart, adjustedEnd } = adjustForTimezone(start, end, timezone);
        
        // Build description from remaining lines
        const descriptionLines = [];
        for (let i = 0; i < lines.length; i++) {
          if (i !== 0 && i !== dateLineIndex && i !== timeLineIndex) {
            descriptionLines.push(lines[i]);
          }
        }
        
        return {
          title,
          start: adjustedStart,
          end: adjustedEnd,
          description: descriptionLines.join('\n')
        };
      }
      
      // Build description from remaining lines
      const descriptionLines = [];
      for (let i = 0; i < lines.length; i++) {
        if (i !== 0 && i !== dateLineIndex && i !== timeLineIndex) {
          descriptionLines.push(lines[i]);
        }
      }
      
      return {
        title,
        start,
        end,
        description: descriptionLines.join('\n')
      };
    }
    
    // If no time range found, look for single time
    const singleTimeRegex = /(\d{1,2}(?::\d{2})?)\s*([ap]\.?m\.?)(?:\s+([A-Za-z]{2,3}))?/i;
    const singleTimeMatch = timeStr.match(singleTimeRegex);
    
    if (singleTimeMatch) {
      const [_, timeValue, ampm, timezone] = singleTimeMatch;
      
      // Parse time
      const start = new Date(date);
      const [hours, minutes] = timeValue.split(':').map(part => parseInt(part, 10));
      const isPM = /p/i.test(ampm);
      
      start.setHours(
        isPM && hours < 12 ? hours + 12 : hours,
        minutes || 0,
        0,
        0
      );
      
      // Default to 1 hour duration
      const end = new Date(start);
      end.setHours(end.getHours() + 1);
      
      // Adjust for timezone if specified
      if (timezone) {
        const { adjustedStart, adjustedEnd } = adjustForTimezone(start, end, timezone);
        
        // Build description from remaining lines
        const descriptionLines = [];
        for (let i = 0; i < lines.length; i++) {
          if (i !== 0 && i !== dateLineIndex && i !== timeLineIndex) {
            descriptionLines.push(lines[i]);
          }
        }
        
        return {
          title,
          start: adjustedStart,
          end: adjustedEnd,
          description: descriptionLines.join('\n')
        };
      }
      
      // Build description from remaining lines
      const descriptionLines = [];
      for (let i = 0; i < lines.length; i++) {
        if (i !== 0 && i !== dateLineIndex && i !== timeLineIndex) {
          descriptionLines.push(lines[i]);
        }
      }
      
      return {
        title,
        start,
        end,
        description: descriptionLines.join('\n')
      };
    }
  }
  
  return null;
}

/**
 * Parse a block of text as a single event
 */
function parseEventBlock(block: string): ParsedEvent | null {
  // First try to parse as unstructured event
  const unstructuredEvent = parseUnstructuredEvent(block);
  if (unstructuredEvent) {
    return unstructuredEvent;
  }
  
  // Clean up the text
  const cleanText = block.trim().replace(/\s+/g, ' ');
  
  // Try to extract title
  let title = 'Untitled Event';
  let remainingText = cleanText;
  
  // Check for title in parentheses
  const titleParenMatch = cleanText.match(/\(([^)]+)\)/);
  if (titleParenMatch) {
    title = titleParenMatch[1].trim();
    remainingText = cleanText.replace(/\(([^)]+)\)/, '').trim();
  } else {
    // Check for title as first line
    const lines = block.split('\n').map(line => line.trim()).filter(line => line);
    if (lines.length > 1 && !lines[0].match(/\d+[:/-]/)) {
      // First line doesn't contain numbers with time/date separators, likely a title
      title = lines[0];
      remainingText = lines.slice(1).join(' ');
    }
  }
  
  // Extract date and time information
  const dateTimeInfo = extractDateTimeInfo(remainingText);
  if (!dateTimeInfo) {
    return null;
  }
  
  // Create event object
  return {
    title,
    start: dateTimeInfo.start,
    end: dateTimeInfo.end || addHours(dateTimeInfo.start, 1), // Default to 1 hour duration
    description: dateTimeInfo.description
  };
}

/**
 * Parse a single line of text as an event
 */
function parseSingleEvent(line: string): ParsedEvent | null {
  // Try different parsing strategies
  const strategies = [
    parseEventWithDashAndParentheses,
    parseEventWithColonAndParentheses,
    parseEventWithTimeRange,
    parseEventWithSimpleDateTimeTitle
  ];

  for (const strategy of strategies) {
    try {
      const event = strategy(line);
      if (event) return event;
    } catch (error) {
      // Try next strategy
    }
  }

  return null;
}

/**
 * Extract date and time information from text
 */
function extractDateTimeInfo(text: string): { start: Date; end?: Date; description?: string } | null {
  // Look for date patterns
  const datePatterns = [
    // Saturday, February 8, 2025
    /([A-Za-z]+day,\s+[A-Za-z]+\s+\d{1,2},\s+\d{4})/i,
    // February 8, 2025
    /([A-Za-z]+\s+\d{1,2},\s+\d{4})/i,
    // 2/8/2025
    /(\d{1,2}\/\d{1,2}\/\d{4})/i,
    // 02-08-2025
    /(\d{1,2}-\d{1,2}-\d{4})/i
  ];

  let dateStr = '';
  for (const pattern of datePatterns) {
    const match = text.match(pattern);
    if (match) {
      dateStr = match[1];
      break;
    }
  }

  if (!dateStr) {
    return null;
  }

  // Look for time patterns
  const timePatterns = [
    // 5:30-8 p.m. MT
    /(\d{1,2}(?::\d{2})?)\s*(?:-|to)\s*(\d{1,2}(?::\d{2})?)\s*([ap]\.?m\.?)(?:\s+([A-Za-z]{2,3}))?/i,
    // 5:30 p.m. - 8:00 p.m. MT
    /(\d{1,2}(?::\d{2})?)\s*([ap]\.?m\.?)\s*(?:-|to)\s*(\d{1,2}(?::\d{2})?)\s*([ap]\.?m\.?)(?:\s+([A-Za-z]{2,3}))?/i,
    // 5:30 p.m. MT
    /(\d{1,2}(?::\d{2})?)\s*([ap]\.?m\.?)(?:\s+([A-Za-z]{2,3}))?/i
  ];

  let startTime = '';
  let endTime = '';
  let startAmPm = '';
  let endAmPm = '';
  let timezone = '';

  for (const pattern of timePatterns) {
    const match = text.match(pattern);
    if (match) {
      if (match.length === 5) {
        // Format: 5:30-8 p.m. MT
        startTime = match[1];
        endTime = match[2];
        startAmPm = match[3];
        endAmPm = match[3];
        timezone = match[4] || '';
      } else if (match.length === 6) {
        // Format: 5:30 p.m. - 8:00 p.m. MT
        startTime = match[1];
        startAmPm = match[2];
        endTime = match[3];
        endAmPm = match[4];
        timezone = match[5] || '';
      } else if (match.length === 4) {
        // Format: 5:30 p.m. MT
        startTime = match[1];
        startAmPm = match[2];
        timezone = match[3] || '';
      }
      break;
    }
  }

  // Parse date
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    return null;
  }

  // Parse start time
  let start = new Date(date);
  if (startTime) {
    const [hours, minutes] = startTime.split(':').map(part => parseInt(part, 10));
    const isPM = /p/i.test(startAmPm);
    
    start.setHours(
      isPM && hours < 12 ? hours + 12 : hours,
      minutes || 0,
      0,
      0
    );
  } else {
    // Default to 9 AM if no time specified
    start.setHours(9, 0, 0, 0);
  }

  // Parse end time
  let end;
  if (endTime) {
    end = new Date(date);
    const [hours, minutes] = endTime.split(':').map(part => parseInt(part, 10));
    const isPM = /p/i.test(endAmPm);
    
    end.setHours(
      isPM && hours < 12 ? hours + 12 : hours,
      minutes || 0,
      0,
      0
    );
  } else {
    // Default to 1 hour duration
    end = new Date(start);
    end.setHours(end.getHours() + 1);
  }

  // Adjust for timezone if specified
  if (timezone) {
    const result = adjustForTimezone(start, end, timezone);
    start = result.adjustedStart;
    end = result.adjustedEnd;
  }

  // Extract any additional description
  let description = '';
  const priceMatch = text.match(/\$(\d+)/);
  if (priceMatch) {
    description = `Price: $${priceMatch[1]}`;
  }

  return { start, end, description };
}

function parseEventWithDashAndParentheses(line: string): ParsedEvent | null {
  // Format: Wed, July 9 – 10am MT (The Sales Process)
  const dashRegex = /(.+)(?:–|-)\s*(.+?)\s*(?:\((.+)\))?$/;
  const match = line.match(dashRegex);
  
  if (!match) return null;
  
  const [_, datePart, timePart, title] = match;
  
  if (!datePart || !timePart) return null;
  
  const dateTimeStr = `${datePart.trim()} ${timePart.trim()}`;
  const { start, end, timezone } = parseDateTime(dateTimeStr);
  
  if (!start) return null;
  
  // Default event duration is 1 hour if no end time
  const eventEnd = end || addHours(start, 1);
  
  // Adjust for timezone if specified
  if (timezone) {
    const { adjustedStart, adjustedEnd } = adjustForTimezone(start, eventEnd, timezone);
    return {
      title: title?.trim() || 'Untitled Event',
      start: adjustedStart,
      end: adjustedEnd
    };
  }
  
  return {
    title: title?.trim() || 'Untitled Event',
    start,
    end: eventEnd
  };
}

function parseEventWithColonAndParentheses(line: string): ParsedEvent | null {
  // Format: Wed, July 9: 10am MT (The Sales Process)
  const colonRegex = /(.+?):\s*(.+?)\s*(?:\((.+)\))?$/;
  const match = line.match(colonRegex);
  
  if (!match) return null;
  
  const [_, datePart, timePart, title] = match;
  
  if (!datePart || !timePart) return null;
  
  const dateTimeStr = `${datePart.trim()} ${timePart.trim()}`;
  const { start, end, timezone } = parseDateTime(dateTimeStr);
  
  if (!start) return null;
  
  // Default event duration is 1 hour if no end time
  const eventEnd = end || addHours(start, 1);
  
  // Adjust for timezone if specified
  if (timezone) {
    const { adjustedStart, adjustedEnd } = adjustForTimezone(start, eventEnd, timezone);
    return {
      title: title?.trim() || 'Untitled Event',
      start: adjustedStart,
      end: adjustedEnd
    };
  }
  
  return {
    title: title?.trim() || 'Untitled Event',
    start,
    end: eventEnd
  };
}

function parseEventWithTimeRange(line: string): ParsedEvent | null {
  // Format: Saturday, February 8, 2025 5:30-8 p.m. (Event Title)
  const timeRangeRegex = /(.+?)\s+(\d{1,2}(?::\d{2})?)\s*(?:-|to)\s*(\d{1,2}(?::\d{2})?)\s*([ap]\.?m\.?)(?:\s+([A-Za-z]{2,3}))?(?:\s*\((.+)\))?/i;
  const match = line.match(timeRangeRegex);
  
  if (!match) return null;
  
  const [_, datePart, startTimePart, endTimePart, ampm, timezone, title] = match;
  
  if (!datePart || !startTimePart || !endTimePart) return null;
  
  // Parse date
  const date = new Date(datePart.trim());
  if (isNaN(date.getTime())) return null;
  
  // Parse start time
  const start = new Date(date);
  const [startHours, startMinutes] = startTimePart.split(':').map(part => parseInt(part, 10));
  const isPM = /p/i.test(ampm);
  
  start.setHours(
    isPM && startHours < 12 ? startHours + 12 : startHours,
    startMinutes || 0,
    0,
    0
  );
  
  // Parse end time
  const end = new Date(date);
  const [endHours, endMinutes] = endTimePart.split(':').map(part => parseInt(part, 10));
  
  end.setHours(
    isPM && endHours < 12 ? endHours + 12 : endHours,
    endMinutes || 0,
    0,
    0
  );
  
  // Adjust for timezone if specified
  if (timezone) {
    const { adjustedStart, adjustedEnd } = adjustForTimezone(start, end, timezone);
    return {
      title: title?.trim() || 'Untitled Event',
      start: adjustedStart,
      end: adjustedEnd
    };
  }
  
  return {
    title: title?.trim() || 'Untitled Event',
    start,
    end
  };
}

function parseEventWithSimpleDateTimeTitle(line: string): ParsedEvent | null {
  // Try to extract title from parentheses first
  const titleMatch = line.match(/\(([^)]+)\)/);
  const title = titleMatch ? titleMatch[1].trim() : 'Untitled Event';
  
  // Remove the title part to focus on date/time
  const dateTimePart = titleMatch 
    ? line.replace(/\([^)]+\)/, '').trim() 
    : line.trim();
  
  // Try to parse the remaining text as a date
  const { start, end, timezone } = parseDateTime(dateTimePart);
  
  if (!start) return null;
  
  // Default event duration is 1 hour if no end time
  const eventEnd = end || addHours(start, 1);
  
  // Adjust for timezone if specified
  if (timezone) {
    const { adjustedStart, adjustedEnd } = adjustForTimezone(start, eventEnd, timezone);
    return {
      title,
      start: adjustedStart,
      end: adjustedEnd
    };
  }
  
  return { 
    title, 
    start, 
    end: eventEnd 
  };
}

function parseDateTime(dateTimeStr: string): { start: Date | null; end?: Date | null; timezone?: string } {
  // Extract timezone if present
  let timezone = '';
  const timezoneMatch = dateTimeStr.match(/\b(EST|EDT|CST|CDT|MST|MDT|PST|PDT|ET|CT|MT|PT)\b/i);
  if (timezoneMatch) {
    timezone = timezoneMatch[1];
    // Remove timezone from string for cleaner parsing
    dateTimeStr = dateTimeStr.replace(timezoneMatch[0], '').trim();
  }
  
  // Try to parse the date string
  const date = new Date(dateTimeStr);
  
  // Check if the date is valid
  if (!isNaN(date.getTime())) {
    return { start: date, timezone };
  }
  
  // Try more specific parsing for common formats
  
  // Format: Wed, July 9 10am
  const monthDayTimeRegex = /(?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),\s*)?([A-Za-z]+)\s+(\d{1,2})(?:,\s*(\d{4}))?\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm|AM|PM)/;
  const monthDayTimeMatch = dateTimeStr.match(monthDayTimeRegex);
  
  if (monthDayTimeMatch) {
    const [_, month, day, yearStr, hour, minute, ampm] = monthDayTimeMatch;
    
    // Get current year if not specified
    const year = yearStr ? parseInt(yearStr) : new Date().getFullYear();
    
    // Parse month
    const monthIndex = getMonthIndex(month);
    
    // Parse hour (12-hour format)
    let hourValue = parseInt(hour);
    if ((ampm.toLowerCase() === 'pm') && hourValue < 12) {
      hourValue += 12;
    } else if ((ampm.toLowerCase() === 'am') && hourValue === 12) {
      hourValue = 0;
    }
    
    // Create date object
    const parsedDate = new Date(year, monthIndex, parseInt(day), hourValue, minute ? parseInt(minute) : 0);
    
    return { start: parsedDate, timezone };
  }
  
  // Format: 7/9/2025 10am
  const numericDateTimeRegex = /(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm|AM|PM)/;
  const numericDateTimeMatch = dateTimeStr.match(numericDateTimeRegex);
  
  if (numericDateTimeMatch) {
    const [_, month, day, year, hour, minute, ampm] = numericDateTimeMatch;
    
    // Parse hour (12-hour format)
    let hourValue = parseInt(hour);
    if ((ampm.toLowerCase() === 'pm') && hourValue < 12) {
      hourValue += 12;
    } else if ((ampm.toLowerCase() === 'am') && hourValue === 12) {
      hourValue = 0;
    }
    
    // Create date object (month is 0-indexed in JS Date)
    const parsedDate = new Date(
      parseInt(year), 
      parseInt(month) - 1, 
      parseInt(day), 
      hourValue, 
      minute ? parseInt(minute) : 0
    );
    
    return { start: parsedDate, timezone };
  }
  
  // Format: Wed, July 9 10am-11am
  const timeRangeRegex = /(?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),\s*)?([A-Za-z]+)\s+(\d{1,2})(?:,\s*(\d{4}))?\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm|AM|PM)\s*(?:-|to)\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm|AM|PM)/;
  const timeRangeMatch = dateTimeStr.match(timeRangeRegex);
  
  if (timeRangeMatch) {
    const [_, month, day, yearStr, startHour, startMinute, startAmPm, endHour, endMinute, endAmPm] = timeRangeMatch;
    
    // Get current year if not specified
    const year = yearStr ? parseInt(yearStr) : new Date().getFullYear();
    
    // Parse month
    const monthIndex = getMonthIndex(month);
    
    // Parse start hour (12-hour format)
    let startHourValue = parseInt(startHour);
    if ((startAmPm.toLowerCase() === 'pm') && startHourValue < 12) {
      startHourValue += 12;
    } else if ((startAmPm.toLowerCase() === 'am') && startHourValue === 12) {
      startHourValue = 0;
    }
    
    // Parse end hour (12-hour format)
    let endHourValue = parseInt(endHour);
    if ((endAmPm.toLowerCase() === 'pm') && endHourValue < 12) {
      endHourValue += 12;
    } else if ((endAmPm.toLowerCase() === 'am') && endHourValue === 12) {
      endHourValue = 0;
    }
    
    // Create date objects
    const startDate = new Date(year, monthIndex, parseInt(day), startHourValue, startMinute ? parseInt(startMinute) : 0);
    const endDate = new Date(year, monthIndex, parseInt(day), endHourValue, endMinute ? parseInt(endMinute) : 0);
    
    return { start: startDate, end: endDate, timezone };
  }
  
  return { start: null };
}

/**
 * Adjust dates for timezone differences
 * This converts from the specified timezone to the user's local timezone
 */
function adjustForTimezone(start: Date, end: Date, timezone: string): { adjustedStart: Date; adjustedEnd: Date } {
  // Define timezone offsets in hours from UTC
  const timezoneOffsets: Record<string, number> = {
    'EST': -5, // Eastern Standard Time
    'EDT': -4, // Eastern Daylight Time
    'CST': -6, // Central Standard Time
    'CDT': -5, // Central Daylight Time
    'MST': -7, // Mountain Standard Time
    'MDT': -6, // Mountain Daylight Time
    'PST': -8, // Pacific Standard Time
    'PDT': -7, // Pacific Daylight Time
    'ET': -4,  // Eastern Time (assumed EDT during summer)
    'CT': -5,  // Central Time (assumed CDT during summer)
    'MT': -6,  // Mountain Time (assumed MDT during summer)
    'PT': -7   // Pacific Time (assumed PDT during summer)
  };
  
  // Normalize timezone to uppercase
  const normalizedTimezone = timezone.toUpperCase();
  
  // If timezone is not recognized, return original dates
  if (!(normalizedTimezone in timezoneOffsets)) {
    return { adjustedStart: start, adjustedEnd: end };
  }
  
  // Get the source timezone offset in hours
  const sourceOffset = timezoneOffsets[normalizedTimezone];
  
  // Get the target timezone (EDT for Eastern Time)
  const targetTimezone = 'EDT';
  const targetOffset = timezoneOffsets[targetTimezone];
  
  // Calculate the difference in hours between source and target timezones
  const hoursDifference = targetOffset - sourceOffset;
  
  // Create new date objects to avoid modifying the originals
  const adjustedStart = new Date(start);
  const adjustedEnd = new Date(end);
  
  // Apply the timezone difference
  adjustedStart.setHours(start.getHours() + hoursDifference);
  adjustedEnd.setHours(end.getHours() + hoursDifference);
  
  return { adjustedStart, adjustedEnd };
}

function getMonthIndex(monthName: string): number {
  const months: Record<string, number> = {
    'january': 0, 'jan': 0,
    'february': 1, 'feb': 1,
    'march': 2, 'mar': 2,
    'april': 3, 'apr': 3,
    'may': 4,
    'june': 5, 'jun': 5,
    'july': 6, 'jul': 6,
    'august': 7, 'aug': 7,
    'september': 8, 'sep': 8, 'sept': 8,
    'october': 9, 'oct': 9,
    'november': 10, 'nov': 10,
    'december': 11, 'dec': 11
  };
  
  return months[monthName.toLowerCase()] || 0;
}